import React, { useRef, useState, useEffect, useCallback } from "react";

import Webcam from "react-webcam";

import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useFileUploadContext } from "../../context/FileUpload";
import { ViewFinder } from "../../pages/QRCodeScanner";

const CaptureTitle = styled("h5")({
  fontSize: "3.5rem",
  margin: "7rem 10rem 4rem",
  color: "#000000",
  textAlign: "center",
});

const CaptureError = styled("div")({
  width: "100%",
  height: "100%",
  padding: "10rem",
  fontSize: "4.5rem",
  color: "red",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  textAlign: "center",
});

const CaptureContainer = styled("div")({
  padding: "2rem",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  fontSize: "5rem",

  "& svg": {
    width: "calc(100% - 4rem) !important",
    height: "calc(100% - 4rem) !important",
    top: "2rem !important",
    left: "2rem !important",
  },

  "@media screen and (orientation: landscape)": {
    flexDirection: "row",
    justifyContent: "center",

    "& > :first-of-type": {
      marginRight: "2rem",
    },
  },
});

const Countdown = styled("div")({
  position: "absolute",
  top: 0,
  left: 0,
  zIndex: 1,
  width: "100%",
  height: "100%",
  display: "flex",
  justifyContent: "end",
  fontSize: "7rem",
  color: "white",
  padding: "2rem 4.5rem",
});

const videoConstraints = {
  aspectRatio: 1,
  facingMode: { ideal: "environment" },
};

const CameraCapture: React.FC<{ onAccept: (photo: string) => void }> = ({
  onAccept,
}) => {
  const { t } = useTranslation(["photo"]);

  const { onUpload } = useFileUploadContext();

  const webcamRef = useRef<Webcam | null>(null);

  const [imgSrc, setImgSrc] = useState<string | null>(null);
  const [countdown, setCountdown] = useState<number>(0);

  const [captureError, setCaptureError] = useState<boolean>(false);

  const capturePhoto = useCallback(() => {
    if (webcamRef.current && webcamRef.current.getScreenshot) {
      const imageSrc = webcamRef.current.getScreenshot();
      setImgSrc(imageSrc);
      setCountdown(0);
    }
  }, []);

  const dataURLtoFile = (dataURL: string, filename: string): File | null => {
    const arr = dataURL.split(",");

    const mimeMatch = arr[0].match(/:(.*?);/);

    if (!mimeMatch) {
      return null;
    }

    const mime = mimeMatch[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    // eslint-disable-next-line no-plusplus
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mime });
  };

  const handleError = () => {
    setCaptureError(true);

    setTimeout(() => {
      onAccept("");
      setCaptureError(false);
    }, 5000);
  };

  const handleUpload = () => {
    if (imgSrc) {
      const file = dataURLtoFile(imgSrc, `photo_${Date.now()}.png`);

      if (!file) {
        handleError();
        return;
      }

      const fileUploadData = {
        data: imgSrc,
        file,
      };

      onUpload(fileUploadData)
        .then((response) => {
          onAccept(response?.[0].file.id);
        })
        .catch(() => {
          handleError();
        });
    }
  };

  useEffect(() => {
    if (!imgSrc) {
      setCountdown(6);

      const countdownInterval = setInterval(() => {
        setCountdown((prevCountdown) => {
          if (prevCountdown <= 1) {
            clearInterval(countdownInterval);
            capturePhoto();
            return 0;
          }
          return prevCountdown - 1;
        });
      }, 1000);
    }
  }, [capturePhoto, imgSrc]);

  return (
    <div className="w-100 h-100 ">
      {captureError ? (
        <CaptureError>{t("photo:error")}</CaptureError>
      ) : (
        <>
          <CaptureTitle>{t("photo:subtitle")}</CaptureTitle>
          <CaptureContainer>
            <div className="position-relative mb-5">
              <Webcam
                audio={false}
                ref={webcamRef}
                screenshotFormat="image/png"
                videoConstraints={videoConstraints}
                style={{
                  display: "block",
                  width: "65rem",
                  height: "65srem",
                  borderRadius: "1rem",
                }}
              />
              <ViewFinder />
              {countdown !== 0 && <Countdown>{countdown}</Countdown>}
            </div>
            {imgSrc && (
              <img className="rounded-4 mt-0" src={imgSrc} alt="Captured" />
            )}
          </CaptureContainer>
          {imgSrc && (
            <>
              <Button
                variant="contained"
                className="px-2 mt-2 me-5"
                style={{ width: "31rem" }}
                onClick={() => setImgSrc(null)}
              >
                {t("photo:button.recapture")}
              </Button>
              <Button
                variant="contained"
                className="px-2 mt-2"
                style={{ width: "31rem" }}
                onClick={handleUpload}
              >
                {t("photo:button.continue")}
              </Button>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default CameraCapture;
